/**
 * CommandHome — §1 "surveillance operations room" dashboard variant.
 *
 * Layout:
 *   Row 1: 4 compact mono stat tiles (events tile has a 7-day BarMini micro-bar)
 *   Row 2: [3/5] map panel (portal anchor, min-h-[420px])
 *          [2/5] live activity feed (mono timestamps, camera, chips; newest-first;
 *                one-shot amber pulse on a newly-arrived event via lastSeenId state)
 *
 * Data contract: receives ONLY what DashboardPage already computed — NO new fetches.
 */

import { useState, useEffect } from 'react'
import { Camera, Activity, MapPin, Wifi, WifiOff, FolderOpen } from 'lucide-react'
import type { Event, CameraMapPin } from '@/types'
import { eventsPerDay } from '@/lib/eventsPerDay'
import { BarMini } from '@/components/charts/BarMini'
import { DetectionChip } from '@/components/ui/detection-chip'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export interface CommandHomeProps {
  /** Cameras query results (page 1) */
  cameras: { id: number; is_active: boolean }[]
  totalCameras: number
  camerasLoading: boolean
  /** Events query results (page 1) */
  events: Event[]
  totalEvents: number
  eventsLoading: boolean
  /** Map pins for determining whether to render the anchor or a placeholder */
  mapPins: CameraMapPin[]
  /** Callback ref: call with the div element for the portal target; null on unmount */
  setAnchor: (el: HTMLDivElement | null) => void
}

// ── Compact mono stat tile ────────────────────────────────────────────────────

interface StatTileProps {
  label: string
  value: number | string
  loading?: boolean
  chart?: { date: string; count: number }[]
  icon: React.ReactNode
}

function StatTile({ label, value, loading, chart, icon }: StatTileProps) {
  return (
    <div className="bg-card border border-border rounded-sm p-3 flex flex-col gap-1.5 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</span>
        <span className="text-muted-foreground/50 flex-shrink-0">{icon}</span>
      </div>
      {loading ? (
        <div className="shimmer h-5 w-12 rounded" />
      ) : (
        <span className="font-mono text-xl tabular-nums text-foreground leading-none">{value}</span>
      )}
      {chart && (
        <BarMini
          data={chart}
          className="h-6 w-full mt-0.5 opacity-70"
        />
      )}
    </div>
  )
}

// ── Activity feed row ─────────────────────────────────────────────────────────

interface ActivityRowProps {
  event: Event
  isNew: boolean
}

function ActivityRow({ event, isNew }: ActivityRowProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 px-2 py-1.5 border-b border-border/40',
        isNew && 'animate-[pulse_1s_ease_1]',
      )}
    >
      {/* Mono timestamp */}
      <span className="font-mono text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0 leading-tight mt-0.5">
        {new Date(event.occurred_at).toISOString().slice(11, 19)}
      </span>

      <div className="flex-1 min-w-0 space-y-0.5">
        {/* Camera name */}
        <div className="flex items-center gap-1 min-w-0">
          <Camera className="h-3 w-3 text-muted-foreground/60 flex-shrink-0" />
          <span className="font-mono text-[11px] text-foreground/80 truncate leading-tight">{event.camera_name}</span>
        </div>

        {/* Detection chips */}
        {event.detected_objects && event.detected_objects.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {event.detected_objects.slice(0, 2).map((cls) => (
              <DetectionChip key={cls} cls={cls} type="object" />
            ))}
            {event.detected_objects.length > 2 && (
              <span className="font-mono text-[10px] text-muted-foreground">+{event.detected_objects.length - 2}</span>
            )}
          </div>
        )}
        {event.audio_detections && event.audio_detections.length > 0 && event.detected_objects?.length === 0 && (
          <div className="flex flex-wrap gap-1">
            {event.audio_detections.slice(0, 2).map((ad) => (
              <DetectionChip key={ad.class_name} cls={ad.class_name} type="audio" />
            ))}
          </div>
        )}

        {/* Camera group */}
        {event.camera_group && (
          <div className="flex items-center gap-1">
            <FolderOpen className="h-2.5 w-2.5 text-muted-foreground/40 flex-shrink-0" />
            <span className="font-mono text-[10px] text-muted-foreground/50 truncate">{event.camera_group}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── CommandHome ───────────────────────────────────────────────────────────────

export function CommandHome({
  cameras,
  totalCameras,
  camerasLoading,
  events,
  totalEvents,
  eventsLoading,
  mapPins,
  setAnchor,
}: CommandHomeProps) {
  const { t } = useTranslation('cameras')
  const { t: te } = useTranslation('events')
  const { t: tc } = useTranslation('common')

  // Track the newest event id to trigger the one-shot amber pulse.
  // null = first load (no pulse); once first load is seen, lastSeenId = newestId.
  // When a new event arrives (newestId !== lastSeenId), we pulse for ~1.2s then
  // advance lastSeenId so the pulse is exactly one-shot.
  const [lastSeenId, setLastSeenId] = useState<number | null>(null)
  const [pulsingId, setPulsingId] = useState<number | null>(null)

  const newestEvent = events[0] ?? null
  const newestId = newestEvent?.id ?? null

  useEffect(() => {
    if (newestId === null) return
    if (lastSeenId === null) {
      // First load — mark as seen, no pulse
      setLastSeenId(newestId)
      return
    }
    if (newestId !== lastSeenId) {
      // New event arrived — pulse for 1.2s then clear
      setPulsingId(newestId)
      setLastSeenId(newestId)
      const timer = setTimeout(() => setPulsingId(null), 1200)
      return () => clearTimeout(timer)
    }
  }, [newestId]) // eslint-disable-line react-hooks/exhaustive-deps

  const activeCameras = camerasLoading ? 0 : cameras.filter(c => c.is_active).length
  const onlineCameras = mapPins.filter(p => p.is_online).length

  // 7-day series for events micro-bar — bucket by occurred_at (what rows display)
  const eventSeries = eventsPerDay(events.map(e => ({ created_at: e.occurred_at })), 7, new Date())

  return (
    <div className="space-y-4">
      {/* ── Stat tiles row ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile
          label={t('dashboard.totalCameras')}
          value={totalCameras}
          loading={camerasLoading}
          icon={<Camera className="h-3 w-3" />}
        />
        <StatTile
          label={t('dashboard.detectionEvents')}
          value={totalEvents}
          loading={eventsLoading}
          chart={eventSeries}
          icon={<Activity className="h-3 w-3" />}
        />
        <StatTile
          label={t('dashboard.activeCameras')}
          value={activeCameras}
          loading={camerasLoading}
          icon={<Wifi className="h-3 w-3" />}
        />
        <StatTile
          label={tc('status.online', { defaultValue: 'Online' })}
          value={onlineCameras}
          loading={false}
          icon={<MapPin className="h-3 w-3" />}
        />
      </div>

      {/* ── Main panel: map (3/5) + activity feed (2/5) ── */}
      <div className="grid gap-3 lg:grid-cols-5 min-h-[420px] lg:h-[calc(100dvh-22rem)]">

        {/* Map panel — 3/5 */}
        <div className="lg:col-span-3 bg-card border border-border rounded-sm overflow-hidden flex flex-col min-h-[420px]">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {t('map.title', { defaultValue: 'Camera Map' })}
            </span>
            {mapPins.length > 0 && (
              <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                {mapPins.length} {tc('units.pins', { defaultValue: 'pins' })}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-hidden min-h-0">
            {mapPins.length > 0 ? (
              <div
                ref={setAnchor}
                className="h-full min-h-0 w-full overflow-hidden"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-2 text-center p-4">
                <WifiOff className="h-6 w-6 text-muted-foreground/30" />
                <span className="font-mono text-[11px] text-muted-foreground">{t('map.noCoordinates', { defaultValue: 'No camera coordinates' })}</span>
              </div>
            )}
          </div>
        </div>

        {/* Live activity feed — 2/5 */}
        <div className="lg:col-span-2 bg-card border border-border rounded-sm overflow-hidden flex flex-col h-[320px] lg:h-full">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2 flex-shrink-0">
            <Activity className="h-3.5 w-3.5 text-primary" />
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              {te('title', { defaultValue: 'Activity Feed' })}
            </span>
            {!eventsLoading && totalEvents > 0 && (
              <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                {totalEvents.toLocaleString()} {te('total', { defaultValue: 'total' })}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {eventsLoading ? (
              <div className="space-y-0 p-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex gap-2 px-2 py-1.5 border-b border-border/40">
                    <div className="shimmer h-3 w-14 rounded" />
                    <div className="flex-1 space-y-1">
                      <div className="shimmer h-3 w-24 rounded" />
                      <div className="shimmer h-3 w-16 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : events.length > 0 ? (
              <div>
                {events.slice(0, 20).map((event) => (
                  <ActivityRow
                    key={event.id}
                    event={event}
                    isNew={event.id === pulsingId}
                  />
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-1 text-center p-4">
                <Activity className="h-5 w-5 text-muted-foreground/30" />
                <span className="font-mono text-[11px] text-muted-foreground">{te('noRecentEvents', { defaultValue: 'No events yet' })}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
